import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import type { EdgeLogsResponse } from './diagnosticsTypes';

type DiagnosticsEdgeOperationsPanelProps = {
  deviceId: string;
  canOperate: boolean;
  edgeLogsLoading: boolean;
  edgeLogsError: string | null;
  edgeLogs: EdgeLogsResponse | null;
  edgeLogsText: string;
  edgeLogProgram: string;
  edgeLogLines: number;
  edgeLogSinceSeconds: number;
  edgeLogJsonOnly: boolean;
  edgeLogContains: string;
  edgeLogLevel: string;
  edgeLogStage: string;
  edgeLogCameraId: string;
  edgeLogPipelineId: string;
  edgeLogAutoRefresh: boolean;
  edgeLogRefreshSec: number;
  edgeLogView: 'pretty' | 'raw';
  edgeLogsSectionExpanded: boolean;
  guardrailsAuditLoading: boolean;
  guardrailsAuditError: string | null;
  guardrailsAudit: any | null;
  throttleOverrideLoading: boolean;
  throttleOverrideError: string | null;
  throttleOverride: any | null;
  throttleOverrideTtlSec: number;
  throttleOverrideReason: string;
  uploadersPausedLoading: boolean;
  uploadersPausedError: string | null;
  uploadersPaused: any | null;
  uploadersPausedTtlSec: number;
  uploadersPausedReason: string;
  overridesListLoading: boolean;
  overridesListError: string | null;
  overridesList: any | null;
  coordinationLoading: boolean;
  coordinationError: string | null;
  coordinationStatus: any | null;
  fetchEdgeLogs: () => void;
  copyEdgeLogs: () => void;
  downloadEdgeLogs: () => void;
  setEdgeLogProgram: (value: string) => void;
  setEdgeLogLines: (value: number) => void;
  setEdgeLogSinceSeconds: (value: number) => void;
  setEdgeLogJsonOnly: (value: boolean) => void;
  setEdgeLogContains: (value: string) => void;
  setEdgeLogLevel: (value: string) => void;
  setEdgeLogStage: (value: string) => void;
  setEdgeLogCameraId: (value: string) => void;
  setEdgeLogPipelineId: (value: string) => void;
  setEdgeLogAutoRefresh: (value: boolean) => void;
  setEdgeLogRefreshSec: (value: number) => void;
  setEdgeLogView: (value: 'pretty' | 'raw') => void;
  setEdgeLogsSectionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  fetchGuardrailsAudit: (id: string, limit?: number) => void;
  setGuardrailsAudit: (value: any | null) => void;
  setGuardrailsAuditError: (value: string | null) => void;
  fetchThrottleOverride: (id: string) => void;
  setThrottleOverrideState: (state: 'normal' | 'throttled' | 'halt_evidence') => void;
  clearThrottleOverride: () => void;
  setThrottleOverrideTtlSec: (value: number) => void;
  setThrottleOverrideReason: (value: string) => void;
  fetchUploadersPausedOverride: (id: string) => void;
  setUploadersPausedOverride: () => void;
  clearUploadersPausedOverride: () => void;
  setUploadersPausedTtlSec: (value: number) => void;
  setUploadersPausedReason: (value: string) => void;
  fetchOverridesList: (id: string) => void;
  fetchCoordinationStatus: (id: string) => void;
};

export default function DiagnosticsEdgeOperationsPanel({
  deviceId,
  canOperate,
  edgeLogsLoading,
  edgeLogsError,
  edgeLogs,
  edgeLogsText,
  edgeLogProgram,
  edgeLogLines,
  edgeLogSinceSeconds,
  edgeLogJsonOnly,
  edgeLogContains,
  edgeLogLevel,
  edgeLogStage,
  edgeLogCameraId,
  edgeLogPipelineId,
  edgeLogAutoRefresh,
  edgeLogRefreshSec,
  edgeLogView,
  edgeLogsSectionExpanded,
  guardrailsAuditLoading,
  guardrailsAuditError,
  guardrailsAudit,
  throttleOverrideLoading,
  throttleOverrideError,
  throttleOverride,
  throttleOverrideTtlSec,
  throttleOverrideReason,
  uploadersPausedLoading,
  uploadersPausedError,
  uploadersPaused,
  uploadersPausedTtlSec,
  uploadersPausedReason,
  overridesListLoading,
  overridesListError,
  overridesList,
  coordinationLoading,
  coordinationError,
  coordinationStatus,
  fetchEdgeLogs,
  copyEdgeLogs,
  downloadEdgeLogs,
  setEdgeLogProgram,
  setEdgeLogLines,
  setEdgeLogSinceSeconds,
  setEdgeLogJsonOnly,
  setEdgeLogContains,
  setEdgeLogLevel,
  setEdgeLogStage,
  setEdgeLogCameraId,
  setEdgeLogPipelineId,
  setEdgeLogAutoRefresh,
  setEdgeLogRefreshSec,
  setEdgeLogView,
  setEdgeLogsSectionExpanded,
  fetchGuardrailsAudit,
  setGuardrailsAudit,
  setGuardrailsAuditError,
  fetchThrottleOverride,
  setThrottleOverrideState,
  clearThrottleOverride,
  setThrottleOverrideTtlSec,
  setThrottleOverrideReason,
  fetchUploadersPausedOverride,
  setUploadersPausedOverride,
  clearUploadersPausedOverride,
  setUploadersPausedTtlSec,
  setUploadersPausedReason,
  fetchOverridesList,
  fetchCoordinationStatus,
}: DiagnosticsEdgeOperationsPanelProps) {
  return (
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">Edge Logs</Typography>
            <Typography variant="body2" color="text.secondary">
              Bounded tail from the edge via the Health API proxy (redacted). Expand for filters and output.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                label={
                  edgeLogsLoading
                    ? 'Loading...'
                    : edgeLogs
                      ? `${edgeLogs.count ?? 0} entries | ${edgeLogs.mode || 'unknown'}${edgeLogs.truncated ? ' | truncated' : ''}`
                      : 'Not fetched'
                }
              />
              <Chip
                size="small"
                variant="outlined"
                label={`program: ${edgeLogProgram === 'all' ? 'all' : edgeLogProgram.split(':').pop() || edgeLogProgram}`}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            <Button variant="contained" size="small" onClick={fetchEdgeLogs} disabled={edgeLogsLoading}>
              {edgeLogsLoading ? 'Loading...' : 'Fetch'}
            </Button>
            <Tooltip title="Copy to clipboard">
              <span>
                <IconButton size="small" onClick={copyEdgeLogs} disabled={!edgeLogsText}>
                  <ContentCopyIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download as .txt">
              <span>
                <IconButton size="small" onClick={downloadEdgeLogs} disabled={!edgeLogsText}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setEdgeLogsSectionExpanded((expanded) => !expanded)}
              endIcon={edgeLogsSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {edgeLogsSectionExpanded ? 'Hide panel' : 'Show filters & output'}
            </Button>
          </Box>
        </Box>

        {!edgeLogsSectionExpanded && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Filters, auto-refresh, and the log viewer are hidden. Fetch still uses your last expanded settings.
          </Typography>
        )}

        {edgeLogsError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {edgeLogsError}
          </Alert>
        )}

        {edgeLogs && edgeLogs.ok === false && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {edgeLogs.error || 'edge_logs_failed'} {edgeLogs.detail ? `(${edgeLogs.detail})` : ''}
          </Alert>
        )}

        <Collapse in={edgeLogsSectionExpanded}>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Switch checked={edgeLogAutoRefresh} onChange={(e) => setEdgeLogAutoRefresh(e.target.checked)} />}
              label="Auto-refresh"
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Every</InputLabel>
              <Select
                value={String(edgeLogRefreshSec)}
                label="Every"
                onChange={(e) => setEdgeLogRefreshSec(parseInt(String(e.target.value), 10) || 5)}
              >
                <MenuItem value="2">2s</MenuItem>
                <MenuItem value="5">5s</MenuItem>
                <MenuItem value="10">10s</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Program</InputLabel>
              <Select value={edgeLogProgram} label="Program" onChange={(e) => setEdgeLogProgram(String(e.target.value))}>
                <MenuItem value="all">All (key services)</MenuItem>
                <MenuItem value="edge-services:incident_aggregator">incident_aggregator</MenuItem>
                <MenuItem value="edge-services:warehouse_safety_rules">warehouse_safety_rules</MenuItem>
                <MenuItem value="edge-services:evidence_uploader">evidence_uploader</MenuItem>
                <MenuItem value="edge-services:mqtt_json_publisher">mqtt_json_publisher</MenuItem>
                <MenuItem value="edge-services:fusion_worker">fusion_worker</MenuItem>
                <MenuItem value="edge-services:mqtt_smart_bridge">mqtt_smart_bridge</MenuItem>
                <MenuItem value="edge-services:zone_config_receiver">zone_config_receiver</MenuItem>
                <MenuItem value="edge-services:policy_config_receiver">policy_config_receiver</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Lines</InputLabel>
              <Select
                value={String(edgeLogLines)}
                label="Lines"
                onChange={(e) => setEdgeLogLines(parseInt(String(e.target.value), 10) || 400)}
              >
                <MenuItem value="100">100</MenuItem>
                <MenuItem value="200">200</MenuItem>
                <MenuItem value="400">400</MenuItem>
                <MenuItem value="800">800</MenuItem>
                <MenuItem value="2000">2000</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Since</InputLabel>
              <Select
                value={String(edgeLogSinceSeconds)}
                label="Since"
                onChange={(e) => setEdgeLogSinceSeconds(parseInt(String(e.target.value), 10) || 600)}
              >
                <MenuItem value="60">1m</MenuItem>
                <MenuItem value="300">5m</MenuItem>
                <MenuItem value="600">10m</MenuItem>
                <MenuItem value="1800">30m</MenuItem>
                <MenuItem value="3600">1h</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch checked={edgeLogJsonOnly} onChange={(e) => setEdgeLogJsonOnly(e.target.checked)} />}
              label="JSON only"
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>View</InputLabel>
              <Select value={edgeLogView} label="View" onChange={(e) => setEdgeLogView(e.target.value as 'pretty' | 'raw')}>
                <MenuItem value="pretty">Pretty</MenuItem>
                <MenuItem value="raw">Raw</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Contains"
              value={edgeLogContains}
              onChange={(e) => setEdgeLogContains(e.target.value)}
              placeholder="incident_id=... / camera_id=..."
              sx={{ minWidth: 260 }}
            />

            <TextField
              size="small"
              label="Level"
              value={edgeLogLevel}
              onChange={(e) => setEdgeLogLevel(e.target.value)}
              placeholder="info/warn/error"
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Stage"
              value={edgeLogStage}
              onChange={(e) => setEdgeLogStage(e.target.value)}
              placeholder="ingest/infer/upload"
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label="camera_id"
              value={edgeLogCameraId}
              onChange={(e) => setEdgeLogCameraId(e.target.value)}
              placeholder="cam_..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="pipeline_id"
              value={edgeLogPipelineId}
              onChange={(e) => setEdgeLogPipelineId(e.target.value)}
              placeholder="pipeline_..."
              sx={{ minWidth: 180 }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Source: <strong>{edgeLogs?.mode || 'unknown'}</strong>
              {edgeLogs?.truncated ? ' (truncated)' : ''} | Entries: <strong>{edgeLogs?.count ?? 0}</strong>
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                maxHeight: 360,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
              }}
            >
              {edgeLogsLoading ? 'Loading...' : (edgeLogsText || 'No logs yet. Click Fetch.')}
            </Box>
          </Box>
        </Collapse>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Guardrails audit (edge)</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchGuardrailsAudit(deviceId, 200)}
              disabled={guardrailsAuditLoading}
            >
              {guardrailsAuditLoading ? 'Loading...' : 'Fetch audit'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setGuardrailsAudit(null);
                setGuardrailsAuditError(null);
              }}
              disabled={guardrailsAuditLoading}
            >
              Clear
            </Button>
          </Box>
          {guardrailsAuditError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {guardrailsAuditError}
            </Alert>
          )}
          {guardrailsAudit && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Entries: {String(guardrailsAudit?.count ?? '')}
              </Typography>
              <JsonBox maxHeight={240} value={guardrailsAudit} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Operator override: Throttle state
          </Typography>
          {!canOperate && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Operator actions are disabled for your role. You can still refresh and view current override state.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="TTL (sec)"
              value={String(throttleOverrideTtlSec)}
              onChange={(e) => setThrottleOverrideTtlSec(parseInt(e.target.value, 10) || 900)}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="Reason"
              value={throttleOverrideReason}
              onChange={(e) => setThrottleOverrideReason(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button variant="outlined" size="small" onClick={() => fetchThrottleOverride(deviceId)} disabled={throttleOverrideLoading}>
              Refresh
            </Button>
            <Button variant="contained" size="small" onClick={() => setThrottleOverrideState('normal')} disabled={throttleOverrideLoading || !canOperate}>
              Force normal
            </Button>
            <Button variant="contained" size="small" color="warning" onClick={() => setThrottleOverrideState('throttled')} disabled={throttleOverrideLoading || !canOperate}>
              Force throttled
            </Button>
            <Button variant="contained" size="small" color="error" onClick={() => setThrottleOverrideState('halt_evidence')} disabled={throttleOverrideLoading || !canOperate}>
              Force halt_evidence
            </Button>
            <Button variant="text" size="small" onClick={clearThrottleOverride} disabled={throttleOverrideLoading || !canOperate}>
              Clear override
            </Button>
          </Box>
          {throttleOverrideError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {throttleOverrideError}
            </Alert>
          )}
          {throttleOverride && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Active: {String(throttleOverride?.active ?? throttleOverride?.override?.active ?? '')}{' '}
                {throttleOverride?.override?.until_at ? `until ${String(throttleOverride.override.until_at)}` : ''}
              </Typography>
              <JsonBox maxHeight={200} value={throttleOverride} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Operator override: Pause uploaders
          </Typography>
          {!canOperate && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Operator actions are disabled for your role. You can still refresh and view current override state.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="TTL (sec)"
              value={String(uploadersPausedTtlSec)}
              onChange={(e) => setUploadersPausedTtlSec(parseInt(e.target.value, 10) || 900)}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="Reason"
              value={uploadersPausedReason}
              onChange={(e) => setUploadersPausedReason(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <Button variant="outlined" size="small" onClick={() => fetchUploadersPausedOverride(deviceId)} disabled={uploadersPausedLoading}>
              Refresh
            </Button>
            <Button variant="contained" size="small" color="warning" onClick={setUploadersPausedOverride} disabled={uploadersPausedLoading || !canOperate}>
              Pause uploaders
            </Button>
            <Button variant="text" size="small" onClick={clearUploadersPausedOverride} disabled={uploadersPausedLoading || !canOperate}>
              Resume uploaders
            </Button>
          </Box>
          {uploadersPausedError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {uploadersPausedError}
            </Alert>
          )}
          {uploadersPaused && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Active: {String(uploadersPaused?.active ?? uploadersPaused?.override?.active ?? '')}{' '}
                {uploadersPaused?.override?.until_at ? `until ${String(uploadersPaused.override.until_at)}` : ''}
              </Typography>
              <JsonBox maxHeight={160} value={uploadersPaused} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Active operator overrides (edge)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" size="small" onClick={() => fetchOverridesList(deviceId)} disabled={overridesListLoading}>
              Refresh list
            </Button>
          </Box>
          {overridesListError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {overridesListError}
            </Alert>
          )}
          {overridesList && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Count: {String(overridesList?.count ?? '')}
              </Typography>
              <JsonBox maxHeight={220} value={overridesList} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Coordination / conflicts</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchCoordinationStatus(deviceId)}
              disabled={coordinationLoading}
            >
              {coordinationLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Box>
          {coordinationError && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {coordinationError}
            </Alert>
          )}
          {coordinationStatus && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Summary: {String(coordinationStatus?.summary || '')} | Conflicts:{' '}
                {String(coordinationStatus?.counts?.conflicts ?? '')}
              </Typography>
              <JsonBox maxHeight={200} value={coordinationStatus} />
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function JsonBox({ value, maxHeight }: { value: any; maxHeight: number }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        maxHeight,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: 12,
        whiteSpace: 'pre-wrap',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}
